import React, { useEffect } from 'react';
import { Icon } from '@codex-brightworld/icons';
import { Link } from '@codex-brightworld/basics';
import { formatPrice } from '@codex-brightworld/helpers';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { EnvironmentThemeProvider } from '@codex-brightworld/themes';
import { getCarePlanBenefits } from '../../../appState/ducks/productPage';
import CareplanHeader from './CareplanHeader';

const CpSwatchesWrapper = styled.div`
  font-family: CurrysSans, Helvetica;
  font-size: 14px;
  color: #213038;
  margin-bottom: 16px;
`;

const SwatchesContainer = styled.div`
  display: flex;
`;

const Swatch = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start; 
  flex: 1;
  border-right: 1px solid ${({ theme }) => theme.colors.neutral.grey[2]};
  text-align: center;

  &:first-child {
    flex: 1.8;
  }

  &:last-child {
    border-right: none;
  }
  
  &:only-child {
    strong {
      font-size: 1.1em;
    }
  }
  
  p {
    color: #666666;
    margin: 0;
    line-height: normal;
  }

  p:first-child {
    margin-top: 5px;
  }
  strong {
    font-weight: 700;
  }
`;

const SmallText = styled.p`
  font-size: 12px;
  padding: 0 3px;
`;

const ProsConsContainer = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.neutral.grey[2]};
  border-radius: 10px;
  display: flex;
  margin-top: 24px;
  padding: 16px;
  @media ${(props) => props.theme.breakpoints.mobile} {
    display: block;
  };
`;

const ProsCon = styled.ul`
  flex: 1;
  margin: 0;
  padding: 0;
`;

const ConsCon = styled.ul`
  flex: 1;
  padding: 0;
  @media ${(props) => props.theme.breakpoints.mobile} {
    margin: 15px 0 0;
  }
`;

const GreenTick = styled(Icon)`
  padding-top: 1px;
  path:last-child {
    fill: #4e8130;
  }
`;

const RedCross = styled(Icon)`
  path:last-child {
    fill: #c90404;
  }
`;

const LinkContainer = styled.div`
  margin: 24px 0 32px;
  ${Link} {
    display: block;
    margin-bottom: 16px;
  }
`;

const TextItemContainer = styled.li`
  display: flex;
  margin-bottom: 7px;
  &:last-child {
    margin-bottom: 0px;
  }
  p {
    color: #333;
    line-height: normal;
    font-size: 12px;
    margin: 3px 9px 0 !important;
  }
  svg {
    width: 24px;
    stroke: none;
  }
`;

const StyledLink = styled(Link)`
  color: ${(props) => props.theme.colors.primary.purple};
    &:hover {
    color: ${(props) => props.theme.colors.primary.purple};
  }
`;

const CareplanSwatches = ({
  getCarePlanBenefits, translations, carePlans, carePlansLoading, error
}) => {
  useEffect(() => {
    getCarePlanBenefits();
  }, []);

  const instantReplacementProduct = carePlans && carePlans[0].type === 'REPLACEMENT';
  const carePlanMessages = instantReplacementProduct
    ? translations.instantReplacement
    : (!carePlansLoading && !error) && carePlans[0].messages.slice(0, 3);

  return (
    (!carePlansLoading && !error) && (
    <EnvironmentThemeProvider>
      <CpSwatchesWrapper>
        <CareplanHeader label={translations.header} linkTranslation={translations.moreInfo} />
        <SwatchesContainer>
          {
            carePlans
              .sort((a, b) => (a.price.amountWithVat > b.price.amountWithVat ? 1 : -1))
              .map((option) => (
                <Swatch key={option.id}>
                  <p>{option.title}</p>
                  <strong>
                    {formatPrice(option.price.amountWithVat)}
                    {option.title === 'Monthly' && ' p/m'}
                  </strong>
                  {option.title === 'Monthly' && (
                    <SmallText>
                      {'Annual equivalent '}
                      {formatPrice(option.price.amountWithVat * 12)}
                    </SmallText>
                  )}
                </Swatch>
              ))
          }
        </SwatchesContainer>
        <ProsConsContainer>
          <ProsCon>
            {
              carePlanMessages.map((text) => (
                <TextItemContainer key={text}>
                  <GreenTick icon={'tick'} />
                  <p>{text}</p>
                </TextItemContainer>
              ))
            }
          </ProsCon>
          <ConsCon>
            {
              translations.carePlanExclusions
                .map((text) => (text && (
                  <TextItemContainer key={text}>
                    <RedCross icon={'close'} />
                    <p>{text}</p>
                  </TextItemContainer>
                )
                ))
            }
          </ConsCon>
        </ProsConsContainer>

        <LinkContainer>
          <StyledLink
            data-interaction={'Important information and terms'}
            href={translations.termsAndConditions.url}
            target={'_blank'}
            rel={'noreferrer'}
          >
            {translations.termsAndConditions.title}
          </StyledLink>
          <StyledLink
            data-interaction={'Compare extended warranties'}
            href={translations.compareWarranties.url}
            target={'_blank'}
            rel={'noreferrer'}
          >
            {translations.compareWarranties.title}
          </StyledLink>
        </LinkContainer>
      </CpSwatchesWrapper>
    </EnvironmentThemeProvider>
    )
  );
};

CareplanSwatches.propTypes = {
  carePlans: PropTypes.arrayOf(PropTypes.object),
  error: PropTypes.string,
  getCarePlanBenefits: PropTypes.func.isRequired,
  carePlansLoading: PropTypes.bool.isRequired,
  translations: PropTypes.shape({
    carePlanExclusions: PropTypes.arrayOf(PropTypes.string),
    compareWarranties: PropTypes.objectOf(PropTypes.string),
    termsAndConditions: PropTypes.objectOf(PropTypes.string),
    header: PropTypes.string,
    moreInfo: PropTypes.string,
    instantReplacement: PropTypes.string
  }).isRequired
};

CareplanSwatches.defaultProps = {
  carePlans: null,
  error: null
};

const mapStateToProps = (state) => ({
  carePlansLoading: state.productPage.carePlans.isLoading,
  carePlans: state.productPage.carePlans.data,
  error: state.productPage.carePlans.error
});

const mapDispatchToProps = (dispatch) => bindActionCreators({
  getCarePlanBenefits
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(CareplanSwatches);
